import { IconButton, ListItemText, SvgIcon, Typography, TextField, Chip } from "@mui/material";
import { ReactNode, useContext, useEffect, useId, useRef, useState } from "react";
import { ThemeWrapper, ViewDetailsModal } from "@verity/components";
import {
	CoreButton,
	CoreCard,
	CoreModal,
	coreToast,
	CoreProfileImage,
	CoreMenu,
	CoreMenuItem,
	CoreFilterButton,
	CoreFilterSectionOption,
	CoreSearchField,
	CoreSearchFieldPaper,
} from "@verity/components/core";
import i18n from "../../../i18n";
import dayjs from "dayjs";
import CompositeTable, {
	CompositeTableBodyRow,
	CompositeTableCell,
	CompositeTableHeaderRow,
	CompositeTableSpinner,
	CompositeTableNoResults,
	CompositeTablePaginationWrapper,
} from "../../../components/composite-table";
import MoreVert from "../../../images/portal/more-vert.svg?react";
import SevereFlag from "../../../images/portal/alert-octagon.svg?react";
import MajorFlag from "../../../images/portal/alert-triangle.svg?react";
import MinorFlag from "../../../images/portal/alert-circle.svg?react";
import IdVerification from "../../../images/portal/id-verification.svg?react";
import RoomScan from "../../../results/components/Resources/room-scan.svg?react";
import styles from "./LivePopInPage.module.css";
import { Provider, useSelector } from "react-redux";
import createStore, { RootState } from "./store";
import {
	PopInSessionsQueryParams,
	useGetPopInSessionsQuery,
	useGetProctoringSessionByLiveUserSessionIdQuery,
	useGetProctoringSessionConfigurationQuery,
	useGetUserQuery,
	useGetVerityCourseQuery,
	useSelectPopInSessionFlagCounts,
} from "./store/hooks";
import { Clear } from "@mui/icons-material";
import { PopInSession } from "./store/slices/popInSessions";
import { AssessmentName, CourseName } from "./names";
import SelectCourseModal from "./SelectCourseModal";
import { PopInContext } from "./context";
import { createMessageChannelManager } from "@yujacorp/verity-live";
import apiClient from "../../../helpers/api-client";
import { openAuthenticatedTab } from "../../../helpers/open-authenticated";
import {
	ALL_ANALYZED_STATUS,
	ALL_FLAG_SEVERITY,
	AnalyzedStatus,
	FlagSeverity,
	isSessionViewed,
	selectPopInSession,
	SortField,
} from "./store/selectors";
import handlePopInSessionMessage, { PopInSessionMessage } from "./handlePopInSessionMessage";
import VisualizerBanner from "../visualizer-banner";
import { errorLoading, resetState } from "./store/actions";
import Tooltip from "@mui/material/Tooltip";
import StudentIdPopup from "../../../results/components/student-selection/StudentInfo/StudentIdPopup";
import ProctoringResultsPage from "../../../results/components/ProctoringResultsPage";
import { skipToken } from "@reduxjs/toolkit/query";
import { ProctoringOptionProfileV2 } from "@verity/proctoring-option/types";
import TextSkeleton from "../../../utility/TextSkeleton";
import useFetchProctoringData from "../settings/useFetchProctoringData";
import { PortalControllerContext } from "../../context";

function CourseChip({ courseId, onDelete }: { courseId: string; onDelete: () => void }) {
	return (
		<Chip
			className={styles.courseChip}
			label={<CourseName courseId={courseId} />}
			onDelete={onDelete}
			deleteIcon={<Clear />}
		/>
	);
}

function CourseSelector({
	instituteId,
	courseIds,
	onChange,
}: {
	instituteId: string;
	courseIds: string[];
	onChange: (courseIds: string[]) => void;
}) {
	const [dialogShowing, setDialogShowing] = useState(false);

	return (
		<div className={styles.courseSelectors}>
			<TextField
				select
				SelectProps={{
					onOpen: () => setDialogShowing(true),
					open: false,
					displayEmpty: true,
					renderValue: () => i18n.t("portal.components.livePopIn.livePopInPageTSX.selectCourse"),
				}}
				aria-label={i18n.t("portal.components.livePopIn.livePopInPageTSX.selectCourse")}
				style={{ width: i18n.t("portal.components.livePopIn.livePopInPageTSX.selectCourse").length * 12 }}
			>
				<div />
			</TextField>
			{courseIds.map((courseId) => (
				<CourseChip
					key={courseId}
					courseId={courseId}
					onDelete={() => onChange(courseIds.filter((id) => id !== courseId))}
				/>
			))}
			{dialogShowing && (
				<SelectCourseModal
					onClose={() => setDialogShowing(false)}
					instituteId={instituteId}
					onSelect={(id) => {
						if (!courseIds.includes(id)) {
							onChange(courseIds.concat([id]));
						}
					}}
				/>
			)}
		</div>
	);
}

function SessionSearchBar({
	name,
	setName,
	analyzedStatuses,
	setAnalyzedStatuses,
	severities,
	setSeverities,
	ariaControls,
	totalResults,
}: {
	name: string;
	setName: (newName: string) => void;
	analyzedStatuses: AnalyzedStatus[];
	setAnalyzedStatuses: (statuses: AnalyzedStatus[]) => void;
	severities: FlagSeverity[];
	setSeverities: (severities: FlagSeverity[]) => void;
	ariaControls: string;
	totalResults: number;
}) {
	const [analyzeChecked, setAnalyzeChecked] = useState<AnalyzedStatus[]>(analyzedStatuses);
	const [severitiesChecked, setSeveritiesChecked] = useState<FlagSeverity[]>(severities);

	const filterSections: CoreFilterSectionOption[] = [
		{
			sectionTitle: i18n.t("portal.components.livePopIn.livePopInPageTSX.reportStatus"),
			alignment: "top",
			checkboxes: ALL_ANALYZED_STATUS.map((status) => ({
				checked: analyzeChecked.includes(status),
				onChange: (checked) => {
					const newFilter = analyzeChecked.filter((s) => s !== status);
					setAnalyzeChecked(checked ? newFilter.concat([status]) : newFilter);
				},
				children: i18n.t(`portal.components.livePopIn.livePopInPageTSX.${status}`),
			})),
		},
		{
			sectionTitle: i18n.t("portal.components.livePopIn.livePopInPageTSX.flagLevels"),
			alignment: "top",
			checkboxes: ALL_FLAG_SEVERITY.map((status) => ({
				checked: severitiesChecked.includes(status),
				onChange: (checked) => {
					const newFilter = severitiesChecked.filter((s) => s !== status);
					setSeveritiesChecked(checked ? newFilter.concat([status]) : newFilter);
				},
				children: i18n.t(`portal.components.livePopIn.livePopInPageTSX.${status}`),
			})),
		},
	];

	return (
		<CoreSearchFieldPaper>
			<CoreFilterButton
				onApply={() => {
					setAnalyzedStatuses(analyzeChecked);
					setSeverities(severitiesChecked);
				}}
				onClear={() => {
					setAnalyzeChecked([]);
					setSeveritiesChecked([]);
				}}
				filterSections={filterSections}
				filterCount={analyzeChecked.length + severitiesChecked.length}
			/>
			<CoreSearchField
				label={i18n.t("portal.components.livePopIn.livePopInPageTSX.searchTestTaker")}
				onSearch={setName}
				totalResults={totalResults}
				ariaControls={ariaControls}
				showSearchButton
				value={name}
			/>
		</CoreSearchFieldPaper>
	);
}

function RoomScanVideoModal({ open, onClose, videoUrl }: { open: boolean; onClose: () => void; videoUrl: string }) {
	return (
		<CoreModal
			open={open}
			onClose={onClose}
			title={i18n.t("portal.components.livePopIn.livePopInPageTSX.roomScan")}
			buttons={
				<CoreButton color="secondary" onClick={onClose}>
					{i18n.t("portal.components.livePopIn.livePopInPageTSX.close")}
				</CoreButton>
			}
		>
			<video src={videoUrl} controls />
		</CoreModal>
	);
}

function TestTakerCellContents({ session }: { session: PopInSession }) {
	const { studentId, courseId } = session;

	const { data: studentUser, isLoading: userLoading } = useGetUserQuery(studentId ?? "");
	const { data: proctoringSession } = useGetProctoringSessionByLiveUserSessionIdQuery(
		session.studentUserSessionId ?? skipToken,
	);

	const [idModalShowing, setIdModalShowing] = useState(false);
	const [roomScanModalShowing, setRoomScanModalShowing] = useState(false);

	return studentUser ? (
		<>
			<div className={styles.testTakerCell}>
				<CoreProfileImage
					size={32}
					src={studentUser?.profilePictureUrl}
					name={studentUser?.name}
					alt={i18n.t("portal.components.livePopIn.livePopInPageTSX.profilePicture")}
					isLoading={userLoading}
				/>
				<div className={styles.testTakerDetails}>
					<div className={styles.testTakerNameRow}>
						<span className={styles.cellText}>{userLoading ? <TextSkeleton /> : studentUser.name}</span>
						<div className={styles.photoIcons}>
							{proctoringSession?.roomScanVideoUrl && (
								<div className={styles.photoIconWrapper}>
									<Tooltip title={i18n.t("portal.components.livePopIn.livePopInPageTSX.roomScan")}>
										<IconButton className={styles.photoIcon} onClick={() => setRoomScanModalShowing(true)}>
											<SvgIcon>
												<RoomScan />
											</SvgIcon>
										</IconButton>
									</Tooltip>
								</div>
							)}
							{proctoringSession?.idPhotoAvailable && (
								<div className={styles.photoIconWrapper}>
									<Tooltip title={i18n.t("portal.components.livePopIn.livePopInPageTSX.idVerification")}>
										<IconButton className={styles.photoIcon} onClick={() => setIdModalShowing(true)}>
											<SvgIcon>
												<IdVerification />
											</SvgIcon>
										</IconButton>
									</Tooltip>
								</div>
							)}
						</div>
					</div>
					<div className={styles.sessionCourseName}>
						<CourseName courseId={courseId} />
					</div>
				</div>
			</div>
			{proctoringSession && (
				<StudentIdPopup session={proctoringSession} popupState={idModalShowing} setPopupState={setIdModalShowing} />
			)}
			{proctoringSession?.roomScanVideoUrl && (
				<RoomScanVideoModal
					open={roomScanModalShowing}
					onClose={() => setRoomScanModalShowing(false)}
					videoUrl={proctoringSession.roomScanVideoUrl}
				/>
			)}
		</>
	) : null;
}

function AnalyzeModal({
	onClose,
	courseId,
	proctoringSessionId,
	liveSessionId,
}: {
	onClose: () => void;
	courseId: number;
	proctoringSessionId: string;
	liveSessionId: string;
}) {
	return (
		<CoreModal
			open
			onClose={onClose}
			title={i18n.t(`portal.components.livePopIn.livePopInPageTSX.resolveFlagged`)}
			noActions
			minWidth="calc(100% - 10px)"
			widthFixed
			maxHeight="90%"
			heightFixed
			contentMatchModalHeight
			modalVerticalPosition="bottom"
		>
			<ProctoringResultsPage
				courseId={courseId}
				sessionId={proctoringSessionId}
				showFlaggedOnly
				inProgress
				onDataUpdated={() => apiClient.liveClient.markLiveSessionViewed(liveSessionId)}
			/>
		</CoreModal>
	);
}

function ConfigurationModal({
	onClose,
	proctoringOptions,
}: {
	onClose: () => void;
	proctoringOptions: ProctoringOptionProfileV2["proctoringOption"];
}) {
	const { verityInstituteSettings, instituteSettings } = useContext(PortalControllerContext);
	const { applications, extensions } = useFetchProctoringData();

	return (
		<ViewDetailsModal
			onClose={onClose}
			title={i18n.t(`portal.components.livePopIn.livePopInPageTSX.viewConfigSettings`)}
			configuration={{
				verityInstituteSettings,
				optionRelatedInstituteSettings: instituteSettings,
				proctoringOption: proctoringOptions,
				proctoringData: { applications, extensions }
			}}
			profile={{
				proctoringOption: proctoringOptions,
				id: -1, // TODO: make not -1
				type: "assessment",
				name: null,
			}}
		/>
	);
}

function PopInSessionMenu({ session }: { session: PopInSession }) {
	const { data: proctoringSession } = useGetProctoringSessionByLiveUserSessionIdQuery(
		session.studentUserSessionId ?? skipToken,
	);
	const { data: course } = useGetVerityCourseQuery(session.courseId);
	const { data: proctoringOptions } = useGetProctoringSessionConfigurationQuery(
		!proctoringSession || !course
			? skipToken
			: {
					courseId: course.id,
					sessionId: proctoringSession.uuid,
				},
	);

	const menuButtonRef = useRef<HTMLButtonElement>(null);
	const [menuShowing, setMenuShowing] = useState(false);

	const closeMenu = () => setMenuShowing(false);

	const [analyzeModalShowing, setAnalyzeModalShowing] = useState(false);
	const [configurationModalShowing, setConfigurationModalShowing] = useState(false);

	const menuEntries: ReactNode[] = [];
	if (proctoringSession) {
		if (course) {
			menuEntries.push(
				<CoreMenuItem
					key="analyze"
					onClick={async () => {
						setAnalyzeModalShowing(true);
						closeMenu();
					}}
				>
					<ListItemText>{i18n.t("portal.components.livePopIn.livePopInPageTSX.analyze")}</ListItemText>
				</CoreMenuItem>,
			);
		}
		menuEntries.push(
			<CoreMenuItem
				key="pop-in"
				onClick={async () => {
					try {
						await apiClient.liveClient.registerSelfAsProctor(session.id);
						openAuthenticatedTab(
							`/live/pop-in.html?id=${session.id}&proctoringSessionId=${proctoringSession.uuid}`,
						)?.focus();
					} catch (e) {
						console.error(e);
						coreToast.error(i18n.t("result.components.resultsPage.indexTSX.joinPopInFailed"));
					}
					closeMenu();
				}}
			>
				<ListItemText>{i18n.t("portal.components.livePopIn.livePopInPageTSX.livePopIn")}</ListItemText>
			</CoreMenuItem>,
		);
	}
	if (proctoringOptions) {
		menuEntries.push(
			<CoreMenuItem
				key="configuration"
				onClick={async () => {
					setConfigurationModalShowing(true);
					closeMenu();
				}}
			>
				<ListItemText>{i18n.t("portal.components.livePopIn.livePopInPageTSX.configuration")}</ListItemText>
			</CoreMenuItem>,
		);
	}

	return menuEntries.length > 0 ? (
		<>
			<IconButton ref={menuButtonRef} onClick={() => setMenuShowing(true)}>
				<SvgIcon>
					<MoreVert />
				</SvgIcon>
			</IconButton>
			{menuShowing && (
				<CoreMenu open onClose={closeMenu} anchorEl={menuButtonRef.current}>
					{menuEntries}
				</CoreMenu>
			)}
			{proctoringSession && course && analyzeModalShowing && (
				<AnalyzeModal
					onClose={() => setAnalyzeModalShowing(false)}
					courseId={course.id}
					proctoringSessionId={proctoringSession.uuid}
					liveSessionId={session.id}
				/>
			)}
			{proctoringOptions && configurationModalShowing && (
				<ConfigurationModal onClose={() => setConfigurationModalShowing(false)} proctoringOptions={proctoringOptions} />
			)}
		</>
	) : null;
}

function FlagIndicator({ severity, count }: { severity: "severe" | "major" | "minor"; count: number }) {
	const [iconElement, setIconElement] = useState<HTMLDivElement | undefined>(undefined);

	let icon: ReactNode | undefined;
	switch (severity) {
		case "severe":
			icon = <SevereFlag />;
			break;
		case "major":
			icon = <MajorFlag />;
			break;
		case "minor":
			icon = <MinorFlag />;
			break;
		default:
			console.error("unknown severity", severity);
	}

	return (
		<Tooltip
			title={i18n.t(`portal.components.livePopIn.livePopInPageTSX.${severity}`)}
			slotProps={{ popper: { anchorEl: iconElement } }}
		>
			<div className={styles.flagIndicator}>
				<div
					className={`${styles.flagIndicatorIcon} ${styles[severity]}`}
					ref={(el) => setIconElement(el ?? undefined)}
				>
					<SvgIcon>{icon}</SvgIcon>
				</div>
				{String(count).padStart(2, "0")}
			</div>
		</Tooltip>
	);
}

function TableFlagsIndicator({ sessionId }: { sessionId: string }) {
	const { severe, major, minor } = useSelectPopInSessionFlagCounts(sessionId);

	return (
		<div className={styles.flagIndicators}>
			<FlagIndicator severity="severe" count={severe} />
			<FlagIndicator severity="major" count={major} />
			<FlagIndicator severity="minor" count={minor} />
		</div>
	);
}

function PopInSessionRow({ sessionId }: { sessionId: string }) {
	const session = useSelector((state: RootState) => selectPopInSession(state, sessionId));
	const viewed = session ? isSessionViewed(session) : false;

	return session ? (
		<CompositeTableBodyRow className={viewed ? styles.viewedRow : undefined}>
			<CompositeTableCell>
				<TestTakerCellContents session={session} />
			</CompositeTableCell>
			<CompositeTableCell>
				<span className={styles.cellText}>
					<AssessmentName assessmentId={session.assessmentId} />
				</span>
			</CompositeTableCell>
			<CompositeTableCell>
				<span className={styles.cellText}>{dayjs(session.startTime).format("DD/MM/YYYY hh:mm:ss A")}</span>
			</CompositeTableCell>
			<CompositeTableCell>
				<TableFlagsIndicator sessionId={sessionId} />
			</CompositeTableCell>
			<CompositeTableCell>
				<PopInSessionMenu session={session} />
			</CompositeTableCell>
		</CompositeTableBodyRow>
	) : null;
}

export function LivePopInPageContent({
	instituteIds,
	outerIsLoading = false,
	outerIsError = false,
}: {
	instituteIds?: string[];
	outerIsLoading?: boolean;
	outerIsError?: boolean;
}) {
	const [courseIds, setCourseIds] = useState<string[]>([]);
	const [userNameQuery, setUserNameQuery] = useState("");
	const [analyzedStatuses, setAnalyzedStatuses] = useState<AnalyzedStatus[]>([]);
	const [severities, setSeverities] = useState<FlagSeverity[]>([]);
	const [sortBy, setSortBy] = useState<{ key: SortField; direction: "asc" | "desc" }>({
		key: "studentName",
		direction: "asc",
	});
	const [page, setPage] = useState(0);

	useEffect(() => {
		setPage(0);
	}, [instituteIds, courseIds, userNameQuery, analyzedStatuses, severities]);

	const {
		sessionIds,
		isError: sessionsError,
		isLoading: sessionsLoading,
		fulfilledTimeStamp,
	} = useGetPopInSessionsQuery({
		instituteIds,
		courseIds,
		studentName: userNameQuery,
		severities,
		analyzedStatuses,
		sortBy: sortBy?.key,
		sortDirection: sortBy?.direction,
		page,
	});

	const isLoading = outerIsLoading || sessionsLoading;
	const isError = outerIsError || sessionsError;
	const isLoaded = !isLoading && !isError;
	const hasResults = isLoaded && sessionIds.totalElements > 0;

	const initialTime = useRef(new Date());
	const updatedTime = fulfilledTimeStamp ? new Date(fulfilledTimeStamp) : initialTime.current;
	const controlId = useId();

	const hasFilter =
		courseIds.length > 0 ||
		userNameQuery ||
		(analyzedStatuses.length > 0 && analyzedStatuses.length < 3) ||
		severities.length === 1;

	return (
		<ThemeWrapper scopedCssBaselineProps={{ className: styles.livePopInPage }}>
			<div className={styles.pageContainer}>
				<VisualizerBanner />
				<CoreCard boxShadow>
					<div className={styles.titleCard}>
						<div className={styles.descriptionArea}>
							<Typography variant="h1" className={styles.title}>
								{i18n.t("portal.components.livePopIn.livePopInPageTSX.livePopInTitle")}
							</Typography>
							<Typography className={styles.subtitle}>
								{i18n.t("portal.components.livePopIn.livePopInPageTSX.description")}
							</Typography>
						</div>
						<div className={styles.configArea}>
							<div>
								{i18n.t("portal.components.livePopIn.livePopInPageTSX.todayDate")}{" "}
								<span className={styles.currentDate}>
									{updatedTime.toLocaleDateString(undefined, { dateStyle: "full" })},{" "}
									{updatedTime.toLocaleTimeString(undefined, { timeStyle: "medium" })}
								</span>
							</div>
							{instituteIds && instituteIds.length === 0 && (
								<CourseSelector instituteId={instituteIds[0]} courseIds={courseIds} onChange={setCourseIds} />
							)}
						</div>
					</div>
				</CoreCard>
				<SessionSearchBar
					name={userNameQuery}
					setName={setUserNameQuery}
					severities={severities}
					setSeverities={setSeverities}
					analyzedStatuses={analyzedStatuses}
					setAnalyzedStatuses={setAnalyzedStatuses}
					totalResults={sessionIds.results.length}
					ariaControls={controlId}
				/>
				<div className={`${styles.tableArea} ${hasResults ? "" : styles.noResults}`} id={controlId}>
					<CompositeTablePaginationWrapper
						page={page}
						onChange={setPage}
						count={sessionIds.totalPages}
						className={hasResults ? undefined : styles.noResults}
						tableWrapperClassName={hasResults ? undefined : styles.noResults}
					>
						<CompositeTable
							head={
								<CompositeTableHeaderRow
									columns={[
										{
											key: "studentName",
											content: i18n.t("portal.components.livePopIn.livePopInPageTSX.testTaker"),
											sortable: true,
										},
										{
											key: "assessmentName",
											content: i18n.t("portal.components.livePopIn.livePopInPageTSX.assessment"),
											sortable: true,
										},
										{
											key: "startTime",
											content: i18n.t("portal.components.livePopIn.livePopInPageTSX.startDate"),
											sortable: true,
										},
										{
											key: "flagCounts",
											content: i18n.t("portal.components.livePopIn.livePopInPageTSX.flagLevels"),
											sortable: true,
										},
										{
											key: "actions",
											content: i18n.t("portal.components.livePopIn.livePopInPageTSX.actions"),
										},
									]}
									sortBy={sortBy}
									onSortByChange={setSortBy}
									sortRequired
								/>
							}
						>
							{isLoaded &&
								sessionIds &&
								sessionIds.totalElements > 0 &&
								sessionIds.results.map((sessionId) => <PopInSessionRow key={sessionId} sessionId={sessionId} />)}
						</CompositeTable>
						{isLoading && <CompositeTableSpinner />}
						{isError && (
							<CompositeTableNoResults>
								{i18n.t("portal.components.livePopIn.livePopInPageTSX.errorLoading")}
							</CompositeTableNoResults>
						)}
						{isLoaded && sessionIds && sessionIds.totalElements === 0 && (
							<CompositeTableNoResults
								header={hasFilter ? undefined : i18n.t("portal.components.livePopIn.livePopInPageTSX.noResults")}
							>
								{hasFilter
									? i18n.t("portal.components.livePopIn.livePopInPageTSX.noResults")
									: i18n.t("portal.components.livePopIn.livePopInPageTSX.sessionsWillAppear")}
							</CompositeTableNoResults>
						)}
					</CompositeTablePaginationWrapper>
				</div>
			</div>
		</ThemeWrapper>
	);
}

export default function LivePopInPage({ instituteId }: { instituteId: number }) {
	const store = useRef(createStore());
	const messageChannelManager = useRef(
		createMessageChannelManager<PopInSessionsQueryParams>(async ({ instituteIds, courseIds }) => {
			const searchParams = new URLSearchParams();
			instituteIds?.forEach((id) => searchParams.append("instituteId", id));
			courseIds?.forEach((courseId) => searchParams.append("courseId", courseId));
			return new WebSocket(
				`/api/live/liveSession/popIn/ws?${searchParams.toString()}`,
				(await apiClient.generateAuthorizationHeader()).Authorization?.split(" "),
			);
		}, 5),
	);
	useEffect(() => {
		const messageListener = (message: unknown) =>
			handlePopInSessionMessage(store.current, message as PopInSessionMessage);
		messageChannelManager.current.addSubscriber(messageListener);

		messageChannelManager.current.onStateChange = (state) => {
			if (state === "ready") {
				store.current.dispatch(resetState());
			} else if (state === "failed" || state === "closed") {
				store.current.dispatch(errorLoading());
			}
		};

		const beforeUnloadListener = () => {
			messageChannelManager.current.close();
		};
		window.addEventListener("beforeunload", beforeUnloadListener);

		return () => {
			messageChannelManager.current.removeSubscriber(messageListener);
			messageChannelManager.current.onStateChange = undefined;
			window.removeEventListener("beforeunload", beforeUnloadListener);
		};
	}, []);

	const [instituteUuid, setInstituteUuid] = useState<{
		value?: string;
		isLoading: boolean;
		isError: boolean;
	}>({ isLoading: true, isError: false });
	useEffect(() => {
		(async () => {
			try {
				setInstituteUuid({
					value: (await apiClient.institute.get(instituteId)).uuid,
					isLoading: false,
					isError: false,
				});
			} catch (e) {
				console.error(e);
				setInstituteUuid({ isLoading: false, isError: true });
			}
		})();
	}, []);

	return (
		<Provider store={store.current}>
			<PopInContext.Provider
				value={{
					messageChannelManager: messageChannelManager.current,
				}}
			>
				<LivePopInPageContent
					instituteIds={instituteUuid.value ? [instituteUuid.value] : undefined}
					outerIsLoading={instituteUuid.isLoading}
					outerIsError={instituteUuid.isError}
				/>
			</PopInContext.Provider>
		</Provider>
	);
}
